import React, { useCallback } from "react";
import { omit, uniq } from "lodash";
import { useForm } from "react-hook-form";
import { Prompt, useHistory } from "react-router";

import { useQueryParam, NumberParam, withDefault } from "use-query-params";

import FirstStep from "../components/reviews/StepForm/FirstStep";
import SecondStep from "../components/reviews/StepForm/SecondStep";
import Stepper from "../components/reviews/StepForm/Stepper";
import ThirdStep from "../components/reviews/StepForm/ThirdStep";
import { EmptyState, Link, ScreenContainer } from "../components/wombat-ui";
import useNotification from "../hooks/useNotification";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";
import ReviewSummaryModal from "../components/reviews/StepForm/ReviewSummaryModal";
import { formatISO } from "date-fns/esm";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import {
  GetViewerDocument,
  InsertReviewVariables,
  useInsertReview,
} from "../graphql/generated/definitions";

export type FormStepProps = {
  onNext: () => void;
  register: ReturnType<typeof useForm>["register"];
  unregister: ReturnType<typeof useForm>["unregister"];
  setValue: ReturnType<typeof useForm>["setValue"];
  getValues: ReturnType<typeof useForm>["getValues"];
  formState: ReturnType<typeof useForm>["formState"];
  errors: ReturnType<typeof useForm>["errors"];
  trigger: ReturnType<typeof useForm>["trigger"];
  control: ReturnType<typeof useForm>["control"];
  reset: ReturnType<typeof useForm>["reset"];
};

/**
 * Removing `id` since this is beeing used only for mission creation
 * `collaboratorId` is replaced by `collaboratorIds` so that batch creation of missions is made possible
 */
type ReviewFormVariables = Omit<
  InsertReviewVariables["input"],
  "collaboratorId" | "id"
  > & { collaboratorIds: string[] };

type ReviewFormScreenProps = Record<string, never>;

const ReviewFormScreen: React.FC<ReviewFormScreenProps> = () => {
  const t = useTranslations();
  const {
    id: userId,
    isCapableOfInsertingReviews,
    company: { id: companyId },
  } = useViewer();

  useBreadcrumbs([
    {
      to: "/reviews/new",
      text: t("reviews.new") as string,
    },
  ]);

  const [insertReview, { loading }] = useInsertReview({
    refetchQueries: [{ query: GetViewerDocument }],
    awaitRefetchQueries: true,
  });

  const { notifySuccess, notifyError } = useNotification();

  const {
    register,
    unregister,
    setValue,
    getValues,
    handleSubmit,
    control,
    errors,
    trigger,
    formState,
    reset,
  } = useForm<ReviewFormVariables>({
    shouldUnregister: false,
    mode: "onBlur",
    defaultValues: {
      allowTemplateUpdateByCollaborator: true,
      reviewSupervisorsAttributes: [
        { userId, emailNotificationsEnabled: true },
      ],
    },
  });

  const onSubmit = useCallback(
    (data: Partial<ReviewFormVariables>) => {
      const inputVariables: Partial<ReviewFormVariables> = {
        campaignId: data.campaignId,
        reviewSupervisorsAttributes: data.reviewSupervisorsAttributes,
        startDate: formatISO(data.startDate as unknown as Date),
        endDate: data.endDate ? formatISO(data.endDate as unknown as Date) : null,
        title: data.title,
        description: data.description,
        referentIds: data.referentIds,
        reminderGap: data.reminderGap,
        reminderEnabled: data.reminderEnabled === "true",
        schedule: data.schedule,
        allowTemplateUpdateByCollaborator: data.allowTemplateUpdateByCollaborator,
        templateAttributes: {
          companyId,
          title: data.templateAttributes.title,
          sectionsAttributes:
            data.templateAttributes?.sectionsAttributes.map(
              (
                { questionsAttributes, ...restSection },
                sectionIndex
              ) => ({
                ...restSection,
                order: sectionIndex,
                questionsAttributes: questionsAttributes.map(
                  (questionAttributes, index) => {
                    return {
                      ...omit(
                        questionAttributes,
                        "skill",
                        "defaultValueSchema",
                        "digest",
                        "documents"
                      ),
                      expectedTimeUnit: "hours",
                      order: index,
                      documentsBlobIds: uniq((questionAttributes.documents || []).map(({ blobId }) => blobId))
                    };
                  }
                ),
              })
            ) || [],
        },
      };

      if (data.collaboratorIds) {
        const insertions: Promise<any>[] = [];
        data.collaboratorIds.forEach((collaboratorId) => {
          insertions.push(
            insertReview({
              variables: {
                input: {
                  ...inputVariables,
                  collaboratorId: collaboratorId,
                },
              },
            })
          );
        });

        Promise.all(insertions)
          .then(() => {
            notifySuccess({
              title: t("reviews.notification.success.title") as string,
              description: t("reviews.notification.success.body") as string,
            });

            history.push(`/campaigns/${data.campaignId}`);
          })
          .catch(() => {
            notifyError({
              title: t("reviews.notification.error.title") as string,
              description: t("reviews.notification.error.body") as string,
            });
          });
      } else {
        // Review without collaborator
        insertReview({
          variables: {
            input: {
              ...inputVariables,
            },
          },
        })
          .then(() => {
            notifySuccess({
              title: t("reviews.notification.success.title") as string,
              description: t("reviews.notification.success.body") as string,
            });

            history.push(`/campaigns/${data.campaignId}`);
          })
          .catch(() => {
            notifyError({
              title: t("reviews.notification.error.title") as string,
              description: t("reviews.notification.error.body") as string,
            });
          });
      }
    },
    [userId, companyId]
  );

  /**
   * Redirect the user on the page he come from on cancel
   */
  const history = useHistory();

  /**
   * Utility callback to switch steps
   */
  const [activeStep, setActiveStep] = useQueryParam(
    "activeStep",
    withDefault(NumberParam, 0)
  );
  const handleStep = (activeStep) =>
    useCallback(() => {
      setActiveStep(activeStep);
    }, []);
  const handleStepOne = handleStep(0);
  const handleStepTwo = handleStep(1);
  const handleStepThree = handleStep(2);
  const handleStepFour = handleStep(3);

  const handleGoToStepTwo = useCallback(async () => {
    const result =
      (await trigger("collaboratorIds")) && (await trigger("campaignId"));

    if (result) {
      handleStepTwo();
    }
  }, [trigger, handleStepTwo]);

  const handleGoToStepThree = useCallback(async () => {
    const result = await trigger();

    if (result) {
      handleStepThree();
    }
  }, [trigger, handleStepThree]);

  const handleGoToStepFour = useCallback(async () => {
    const result = await trigger();

    if (result) {
      handleStepFour();
    }
  }, [trigger, handleStepFour]);

  return (
    <ScreenContainer
      className="flex"
      error={
        isCapableOfInsertingReviews ? undefined : (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link to="/" variant="button">
                {t("network.backToHome")}
              </Link>
            }
          />
        )
      }
    >
      <Prompt
        message={(location) => {
          return location.pathname.startsWith("/reviews/new") ||
            formState.isSubmitted
            ? true
            : (t("forms.warning.unsavedChangesLeaveWarning") as string);
        }}
      />
      <div className="relative flex flex-col flex-grow text-dark-100">
        <form
          className="flex flex-col flex-grow"
          onSubmit={handleSubmit(onSubmit)}
          id="reviewCreationForm"
        >
          <div
            className={`${
              activeStep === 0 ? "flex flex-grow flex-col" : "hidden"
            }`}
          >
            <FirstStep
              onNext={handleGoToStepTwo}
              {...{
                register,
                unregister,
                setValue,
                getValues,
                trigger,
                control,
                errors,
                formState,
                reset,
              }}
            />
          </div>
          <div
            className={`${
              activeStep === 1 ? "flex flex-grow flex-col" : "hidden"
            }`}
          >
            <SecondStep
              onNext={handleGoToStepThree}
              {...{
                register,
                unregister,
                trigger,
                setValue,
                getValues,
                control,
                errors,
                formState,
                reset,
              }}
            />
          </div>
          <div
            className={`${
              activeStep === 2 || activeStep === 3
                ? "flex flex-grow flex-col"
                : "hidden"
            }`}
          >
            <ThirdStep
              onNext={handleGoToStepFour}
              {...{
                register,
                unregister,
                setValue,
                getValues,
                control,
                errors,
                formState,
                reset,
              }}
            />
          </div>
          <ReviewSummaryModal
            {...{
              register,
              unregister,
              setValue,
              getValues,
              control,
              errors,
              formState,
              reset,
            }}
            loading={loading}
            isOpen={activeStep === 3}
            onClose={handleStepThree}
          />
        </form>
        <Stepper
          activeStepIndex={activeStep}
          steps={[
            {
              description: t("reviews.whoPartakes") as string,
              onClick: handleStepOne,
            },
            {
              description: t("reviews.content") as string,
              onClick: handleStepTwo,
            },
            {
              description: t("reviews.launch") as string,
              onClick: handleStepThree,
            },
          ]}
        />
      </div>
    </ScreenContainer>
  );
};

export default ReviewFormScreen;
